// ─────────────────────────────────────────────────────────────────────────────
// LOI BODY + HTML GENERATORS
// ─────────────────────────────────────────────────────────────────────────────
// Two paths per template type:
//   - generateLoi*Body  → plain text. Stored in outreach_log.loi_body, used
//     by Copy to Clipboard / Log as Sent / preview-edit modal textarea.
//   - generateLoi*Html  → table-based HTML email. Sent via Graph /me/sendMail
//     with contentType: "HTML". Mirrors the visual design Marketing approved.
//
// HTML uses tables + inline styles only — required for broad email-client
// compatibility (Outlook, Gmail, Apple Mail, mobile clients).

// All LOIs are sent from the shared acquisitions mailbox, regardless of who
// is logged in. The sender's name/phone are still personalised (pulled from
// their Graph profile) but the email in the signature + the From header both
// use this address so replies go to the team inbox.
const LOI_FROM_EMAIL = "acquisitions@proximityparks.com";

// Static Maps thumbnail URL builder — inlined here (rather than importing
// from services/googleMaps) so the plain-text/HTML generator stays a
// pure-function module with no runtime deps.
function loiStaticMapUrl(points) {
  const key = process.env.REACT_APP_GOOGLE_GEOCODE_KEY || process.env.REACT_APP_GOOGLE_MAPS_KEY;
  if (!key || !points || points.length === 0) return null;
  const clean = points.filter(p => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (clean.length === 0) return null;
  const markers = clean
    .map((p, i) => `markers=color:red%7Clabel:${i < 26 ? String.fromCharCode(65 + i) : ""}%7C${p.lat},${p.lng}`)
    .join("&");
  const centerZoom = clean.length === 1 ? `center=${clean[0].lat},${clean[0].lng}&zoom=15&` : "";
  return `https://maps.googleapis.com/maps/api/staticmap?${centerZoom}size=520x220&maptype=satellite&scale=2&${markers}&key=${key}`;
}

function escHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fmtDate(d) {
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
function fmtMoney(n) { return `$${Number(n).toLocaleString()}`; }

// ── Salutation helper ─────────────────────────────────────────────────────
// Pulls a first name for "Dear ___,". CoStar contacts often have the company
// name in `contact.name` (e.g. "Roswell & Company", "I.J. Litwak Realty Limited
// Partnership"), so blindly using the first word of `contact.name` produces
// embarrassing salutations. When the name looks like a company, fall back to
// the first token of the email address (e.g. mike@roswellco.com → "Mike").
const COMPANY_KEYWORDS = /\b(llc|l\.l\.c\.|lp|l\.p\.|llp|lllp|inc|inc\.|incorporated|corp|corporation|company|co\.|partnership|realty|properties|propertie|holdings|holding|ltd|limited|trust|group|enterprises|investments|capital|fund|management|associates|venture|ventures|equity|equities|estates|estate|development|developments|partners|gp|park|parks|industrial|business|plaza|center|centre|complex|warehouse|warehouses|distribution|crossing|pointe|village|station|terminal|depot|mall|commerce|commercial|retail|industries|industry|logistics|portfolio|building|buildings)\b|&/i;

function looksLikeCompany(name) {
  if (!name) return false;
  return COMPANY_KEYWORDS.test(name);
}

const GENERIC_EMAIL_LOCALS = new Set(['info', 'admin', 'office', 'contact', 'sales', 'support', 'team', 'hello', 'noreply', 'no-reply', 'mail', 'inquiries', 'leasing']);

function firstNameFromEmail(email) {
  if (!email) return null;
  const local = String(email).split('@')[0];
  if (!local) return null;
  const tokens = local.split(/[._\-+]/).filter(Boolean);
  const first = tokens[0];
  if (!first) return null;
  if (GENERIC_EMAIL_LOCALS.has(first.toLowerCase())) return null;
  // Reject tokens that contain digits (e.g. "ghs100", "user42") — almost
  // never first names, usually employee IDs / aliases.
  if (/\d/.test(first)) return null;
  // Single token ≥ 8 chars likely a mashed username (e.g. "ijlitwak") —
  // skip rather than produce something like "Ijlitwak".
  if (tokens.length === 1 && first.length >= 8) return null;
  if (first.length < 2) return null;
  if (!/^[a-z]/i.test(first)) return null;
  return first[0].toUpperCase() + first.slice(1).toLowerCase();
}

function resolveFirstName(contact) {
  if (!contact) return 'there';
  const name = contact.name;
  if (name && !looksLikeCompany(name)) {
    const first = String(name).trim().split(/\s+/)[0];
    if (first) return first;
  }
  // Company name in contact.name → derive from email
  const fromEmail = firstNameFromEmail(contact.email1 || contact.email2 || contact.email3);
  if (fromEmail) return fromEmail;
  return null;
}

// Returns the full salutation line ("Dear Mike," when a first name is
// known; "Good morning," / "Good afternoon," / "Good evening," based on
// the current time when no usable name exists).
function timeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function buildSalutation(contact) {
  const first = resolveFirstName(contact);
  return first ? `Dear ${first},` : `${timeOfDayGreeting()},`;
}

// Compute the shared offer math used by both plain-text and HTML paths.
function computeOffer(rba, psf, appSettings) {
  const total = Math.round(psf * (rba || 0));
  const depositPct = parseFloat(appSettings.deposit_pct || '0.01');
  const deposit = Math.round(total * depositPct);
  const expiryDays = parseInt(appSettings.offer_expiry_days || '16', 10);
  const today = new Date();
  const expiration = new Date(today.getTime() + expiryDays * 24 * 60 * 60 * 1000);
  const ddPeriod = appSettings.dd_period || '30 Business Days';
  return { total, deposit, today, expiration, ddPeriod };
}

// ── Plain text: single property ────────────────────────────────────────────
export function generateLoiBody(prop, contact, sender, psf, appSettings) {
  const rba = prop.rba || 0;
  const { total, deposit, today, expiration, ddPeriod } = computeOffer(rba, psf, appSettings);
  const salutation = buildSalutation(contact);
  const fullAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(', ');

  const body = `ProximityParks
Letter of Intent
${fmtDate(today)}

${salutation}

My name is ${sender.name}, and I'm reaching out on behalf of ProximityParks USA, a real estate investment firm focused on acquiring infill industrial properties. We have a fully discretionary fund and are actively deploying capital throughout Florida and Texas.

I came across your property and believe it's a strong fit for our current acquisition strategy. I've included below a brief summary of our all-cash, no-contingency offer for your property at ${fullAddr} to express our interest — we're prepared to move quickly with flexible terms tailored to your goals.

Offer Details
Property Address        ${fullAddr}
Estimated SF            ${Number(rba).toLocaleString()} SF
Offer Amount per SF     $${Number(psf).toLocaleString()} / SF
Initial Deposit Amount  ${fmtMoney(deposit)}
Due Diligence Period    ${ddPeriod}
Offer Expiration        ${fmtDate(expiration)}
Total Offer Amount      ${fmtMoney(total)}

More than anything, I'd like to start a conversation and would love the opportunity to introduce myself and ProximityParks. Call or text me anytime!

________________________________
${sender.name} ${sender.phone}
${LOI_FROM_EMAIL}
ProximityParks USA

This letter of intent is non-binding and subject to execution of a formal Purchase and Sale Agreement. Confidential — intended for named recipient only. ProximityParks USA — proximityparks.com`;

  return { body, total, deposit, expiration: expiration.toISOString().slice(0, 10) };
}

// ── HTML: single property ──────────────────────────────────────────────────
// Layout matches the approved Marketing design: red header bar, intro prose
// with bolded brand + offer language, dark-headed offer-details table with a
// red-highlighted Total Offer Amount row, signature card with red left rule.
export function generateLoiHtml(prop, contact, sender, psf, appSettings) {
  const rba = prop.rba || 0;
  const { total, deposit, today, expiration, ddPeriod } = computeOffer(rba, psf, appSettings);
  const salutation = buildSalutation(contact);
  const fullAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(', ');

  const html = baseTemplate({
    todayStr: fmtDate(today),
    bodyHtml: `
      <p style="margin:0 0 16px;">${escHtml(salutation)}</p>
      <p style="margin:0 0 16px;">My name is <strong>${escHtml(sender.name)}</strong>, and I'm reaching out on behalf of <strong>ProximityParks USA</strong>, a real estate investment firm focused on acquiring infill industrial properties. We have a fully discretionary fund and are actively deploying capital throughout Florida and Texas.</p>
      <p style="margin:0 0 24px;">I came across your property and believe it's a strong fit for our current acquisition strategy. I've included below a brief summary of our <strong>all-cash, no-contingency offer</strong> for your property at <strong>${escHtml(fullAddr)}</strong> to express our interest &mdash; we're prepared to move quickly with flexible terms tailored to your goals.</p>
    `,
    offerRows: [
      ['Property Address', escHtml(fullAddr)],
      ['Estimated SF', `${Number(rba).toLocaleString()} SF`],
      ['Offer Amount per SF', `$${Number(psf).toLocaleString()} / SF`],
      ['Initial Deposit Amount', escHtml(fmtMoney(deposit))],
      ['Due Diligence Period', escHtml(ddPeriod)],
      ['Offer Expiration', escHtml(fmtDate(expiration))],
    ],
    totalLabel: 'Total Offer Amount',
    totalValue: escHtml(fmtMoney(total)),
    closingHtml: `<p style="margin:0;">More than anything, I'd like to start a conversation and would love the opportunity to introduce myself and ProximityParks. Call or text me anytime!</p>`,
    sender,
    mapImgUrl: loiStaticMapUrl([{ lat: Number(prop.latitude), lng: Number(prop.longitude) }]),
  });

  return { html, total, deposit, expiration: expiration.toISOString().slice(0, 10) };
}

// ── Plain text: portfolio ──────────────────────────────────────────────────
export function generatePortfolioLoiBody(props, contact, sender, psf, appSettings) {
  const totalSF = props.reduce((sum, p) => sum + (p.rba || 0), 0);
  const { total, deposit, today, expiration, ddPeriod } = computeOffer(totalSF, psf, appSettings);
  const salutation = buildSalutation(contact);

  const propList = props.map((p, i) =>
    `  ${i + 1}. ${[p.address, p.city, p.state].filter(Boolean).join(', ')}  |  ${Number(p.rba || 0).toLocaleString()} SF`
  ).join('\n');

  const body = `ProximityParks
Letter of Intent
${fmtDate(today)}

${salutation}

My name is ${sender.name}, and I'm reaching out on behalf of ProximityParks USA, a real estate investment firm focused on acquiring infill industrial properties. We have a fully discretionary fund and are actively deploying capital throughout Florida and Texas.

We are pleased to present this Letter of Intent to acquire the following portfolio of ${props.length} industrial properties in an all-cash, no-contingency transaction:

${propList}

Portfolio Offer Summary
Total Square Footage    ${Number(totalSF).toLocaleString()} SF
Offer Amount per SF     $${Number(psf).toLocaleString()} / SF
Total Offer Amount      ${fmtMoney(total)}
Initial Deposit Amount  ${fmtMoney(deposit)}
Due Diligence Period    ${ddPeriod}
Offer Expiration        ${fmtDate(expiration)}

We are prepared to move quickly with flexible terms tailored to your goals. More than anything, I'd like to start a conversation and would love the opportunity to introduce myself and ProximityParks. Call or text me anytime!

________________________________
${sender.name} ${sender.phone}
${LOI_FROM_EMAIL}
ProximityParks USA

This letter of intent is non-binding and subject to execution of a formal Purchase and Sale Agreement. Confidential — intended for named recipient only. ProximityParks USA — proximityparks.com`;

  return { body, total, deposit, totalSF, expiration: expiration.toISOString().slice(0, 10) };
}

// ── HTML: portfolio ────────────────────────────────────────────────────────
export function generatePortfolioLoiHtml(props, contact, sender, psf, appSettings) {
  const totalSF = props.reduce((sum, p) => sum + (p.rba || 0), 0);
  const { total, deposit, today, expiration, ddPeriod } = computeOffer(totalSF, psf, appSettings);
  const salutation = buildSalutation(contact);

  // Property list rendered as a sub-table inside the body section.
  const propRowsHtml = props.map((p, i) => {
    const addr = [p.address, p.city, p.state].filter(Boolean).join(', ');
    return `
      <tr>
        <td style="padding:8px 12px; font-size:13px; color:#6a6a6a; border-bottom:1px solid #e4e4e4; width:32px;">${i + 1}.</td>
        <td style="padding:8px 12px; font-size:13px; color:#333333; font-weight:600; border-bottom:1px solid #e4e4e4;">${escHtml(addr)}</td>
        <td align="right" style="padding:8px 12px; font-size:13px; color:#333333; border-bottom:1px solid #e4e4e4;">${Number(p.rba || 0).toLocaleString()} SF</td>
      </tr>`;
  }).join('');

  const html = baseTemplate({
    todayStr: fmtDate(today),
    bodyHtml: `
      <p style="margin:0 0 16px;">${escHtml(salutation)}</p>
      <p style="margin:0 0 16px;">My name is <strong>${escHtml(sender.name)}</strong>, and I'm reaching out on behalf of <strong>ProximityParks USA</strong>, a real estate investment firm focused on acquiring infill industrial properties. We have a fully discretionary fund and are actively deploying capital throughout Florida and Texas.</p>
      <p style="margin:0 0 16px;">We are pleased to present this Letter of Intent to acquire the following portfolio of <strong>${props.length} industrial properties</strong> in an <strong>all-cash, no-contingency</strong> transaction:</p>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse; margin:0 0 24px; border:1px solid #e4e4e4;">
        ${propRowsHtml}
      </table>
    `,
    offerRows: [
      ['Total Square Footage', `${Number(totalSF).toLocaleString()} SF`],
      ['Offer Amount per SF', `$${Number(psf).toLocaleString()} / SF`],
      ['Initial Deposit Amount', escHtml(fmtMoney(deposit))],
      ['Due Diligence Period', escHtml(ddPeriod)],
      ['Offer Expiration', escHtml(fmtDate(expiration))],
    ],
    totalLabel: 'Total Offer Amount',
    totalValue: escHtml(fmtMoney(total)),
    closingHtml: `<p style="margin:0;">We are prepared to move quickly with flexible terms tailored to your goals. More than anything, I'd like to start a conversation and would love the opportunity to introduce myself and ProximityParks. Call or text me anytime!</p>`,
    sender,
    mapImgUrl: loiStaticMapUrl(props.map(p => ({ lat: Number(p.latitude), lng: Number(p.longitude) }))),
  });

  return { html, total, deposit, totalSF, expiration: expiration.toISOString().slice(0, 10) };
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared HTML email shell.
// Tables-only layout, inline styles, no CSS class hooks, no web fonts —
// renders consistently across Outlook (incl. desktop), Gmail, Apple Mail.
// ─────────────────────────────────────────────────────────────────────────────
function baseTemplate({ todayStr, bodyHtml, offerRows, totalLabel, totalValue, closingHtml, sender, mapImgUrl }) {
  const PP_RED = '#e93529';
  const TEXT = '#333333';
  const MUTED = '#6a6a6a';
  const BORDER = '#e4e4e4';
  const BG = '#f4f4f4';
  const TOTAL_BG = '#fdeceb'; // light pink wash behind the Total row

  const offerRowsHtml = offerRows.map(([label, value]) => `
    <tr>
      <td style="padding:10px 14px; font-size:13px; color:${MUTED}; border-bottom:1px solid ${BORDER};">${label}</td>
      <td align="right" style="padding:10px 14px; font-size:13px; color:${TEXT}; font-weight:600; border-bottom:1px solid ${BORDER};">${value}</td>
    </tr>`).join('');

  // Phone line is omitted entirely if the user has no phone on their Graph profile.
  const phoneLine = sender.phone
    ? `<div style="font-size:12px; color:${TEXT}; margin-bottom:2px;">${escHtml(sender.phone)}</div>`
    : '';

  // Montserrat via Google Fonts. Outlook desktop ignores @import / <link>
  // for web fonts and falls back to the inline font-family stack — the
  // fallback (Helvetica/Arial) keeps it readable when Montserrat can't load.
  const FONT = "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif";

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>Letter of Intent</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
<style>
  /* Force Montserrat across nested elements that some clients re-style. */
  body, table, td, p, div, span, a { font-family: ${FONT} !important; }
</style>
</head>
<body style="margin:0; padding:0; background-color:${BG}; font-family: ${FONT}; color:${TEXT};">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${BG};">
    <tr>
      <td align="center" style="padding:24px 12px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff; max-width:600px; width:100%;">
          <!-- Red header -->
          <tr>
            <td style="background-color:${PP_RED}; padding:24px 28px;">
              <div style="color:#ffffff; font-size:22px; font-weight:800; letter-spacing:-0.5px; line-height:1.2;">ProximityParks</div>
              <div style="color:#ffffff; font-size:18px; font-weight:700; margin-top:2px; line-height:1.3;">Letter of Intent</div>
              <div style="color:rgba(255,255,255,0.85); font-size:12px; font-weight:600; margin-top:8px;">${escHtml(todayStr)}</div>
            </td>
          </tr>
          ${mapImgUrl ? `
          <!-- Property location thumbnail (Google Static Maps) -->
          <tr>
            <td style="padding:0; background-color:#000000;">
              <img src="${mapImgUrl}" width="600" alt="Property location map" style="display:block; width:100%; max-width:600px; height:auto; border:0; outline:none; text-decoration:none;" />
            </td>
          </tr>` : ""}
          <!-- Body prose + offer details -->
          <tr>
            <td style="padding:28px; color:${TEXT}; font-size:14px; line-height:1.6;">
              ${bodyHtml}
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse; margin-bottom:24px;">
                <tr>
                  <td colspan="2" style="background-color:#242424; color:#ffffff; padding:12px 14px; font-size:13px; font-weight:700;">Offer Details</td>
                </tr>
                ${offerRowsHtml}
                <tr>
                  <td style="padding:14px; font-size:13px; color:${PP_RED}; font-weight:700; background-color:${TOTAL_BG};">${totalLabel}</td>
                  <td align="right" style="padding:14px; font-size:14px; color:${PP_RED}; font-weight:800; background-color:${TOTAL_BG};">${totalValue}</td>
                </tr>
              </table>
              ${closingHtml}
            </td>
          </tr>
          <!-- Signature card -->
          <tr>
            <td style="padding:0 28px 28px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-top:1px solid ${BORDER};">
                <tr>
                  <td style="padding-top:20px;">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td style="border-left:4px solid ${PP_RED}; padding:2px 0 2px 14px;">
                          <div style="color:${TEXT}; font-size:14px; font-weight:700; margin-bottom:6px;">${escHtml(sender.name)}</div>
                          ${phoneLine}
                          <div style="font-size:12px;"><a href="mailto:${escHtml(LOI_FROM_EMAIL)}" style="color:${PP_RED}; text-decoration:none;">${escHtml(LOI_FROM_EMAIL)}</a></div>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Footer disclaimer -->
          <tr>
            <td style="padding:0 28px 24px; color:#9a9a9a; font-size:10px; line-height:1.5;">
              This letter of intent is non-binding and subject to execution of a formal Purchase and Sale Agreement. Confidential &mdash; intended for named recipient only. ProximityParks USA &mdash; <a href="https://proximityparks.com" style="color:#9a9a9a; text-decoration:underline;">proximityparks.com</a>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}
